// Every word the Plans tab and a plan's page show, in one place for the copy
// review. Today's words (plans/words.ts there, Poulpe-128) where today's
// pages have them; the few new ones are marked NEW. The plan's own words come
// from its body, the states' names from the board.
import type { PhaseState } from './model'
import type { ShownStage } from './state'

/** `912`, `84K`, `12.4M`, `1.2B`: today's compact count (plans/words.ts there). */
export function compact(n: number): string {
  if (n >= 1e9) return `${(n / 1e9).toFixed(1)}B`
  if (n >= 1e6) return `${(n / 1e6).toFixed(1)}M`
  if (n >= 1e3) return `${Math.round(n / 1e3)}K`
  return String(Math.round(n))
}

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec']

/** `8 Oct`, from a day (`2026-10-08`) as written, or from a time on this device's clock; '' for what is neither. */
export function day(value: string | null | undefined): string {
  if (!value) return ''
  const only = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value)
  if (only) return `${Number(only[3])} ${MONTHS[Number(only[2]) - 1] ?? ''}`.trim()
  const t = Date.parse(value)
  if (Number.isNaN(t)) return ''
  const d = new Date(t)
  return `${d.getDate()} ${MONTHS[d.getMonth()]}`
}

export const planWords = {
  // The side menu
  nav: 'Plan',
  overview: 'Overview',
  progress: 'Progress',
  elsewhere: 'Listed elsewhere',
  decisions: 'Decisions in force',
  annex: 'Annex',

  // The numbers
  landed: 'Landed',
  landedSub: (landed: number, total: number) => `${landed} of ${total} issues on main or done`,
  phasesExited: 'Phases exited',
  phasesExitedValue: (exited: number, total: number) => `${exited} of ${total}`,
  phasesExitedSub: 'by their exit checklists',
  inFlight: 'In flight',
  tokens: 'Tokens',
  tokensSub: (measured: number, worked: number) => `measured on ${measured} of ${worked} issues`,
  /** NEW: what the figure counts, and why it is a lower bound (Poulpe-761). */
  tokensTitle: (tokens: number) =>
    `${compact(tokens)} read anew or written; cache reads left out. At least this many: a session's count starts again when its agent reopens.`,

  // A phase's Dispatch action
  dispatch: 'Dispatch',
  dispatchPhase: (title: string) => `Dispatch ${title}`,
  dispatchHint: 'Start a dispatcher session that hands this phase’s open issues to workers',

  // A phase's waits
  stillWaiting: 'Its issues have landed: still waiting?',

  // Progress
  chartTitle: 'Issues by phase',
  chartLabel: 'Issues by phase and stage',
  stage: { done: 'Done', main: 'On main', flight: 'In flight', todo: 'Not started' } satisfies Record<ShownStage, string>,
  segmentTip: (phase: string, stage: string, n: number, total: number) => `${phase} · ${stage}: ${n} of ${total}`,
  segmentLabel: (stage: string, n: number, total: number) => `${stage}: ${n} of ${total}`,
  barValue: (landed: number, total: number) => `${landed}/${total}`,
  noIssuesYet: 'no issues yet',
  asTable: 'As a table',
  phase: 'Phase',
  issues: 'Issues',

  // A phase
  phaseState: {
    parked: 'Parked',
    exited: 'Exited',
    exit_pending: 'Exit pending',
    in_progress: 'In progress',
    not_started: 'Not started',
  } satisfies Record<PhaseState, string>,
  statLanded: (landed: number, total: number) => [`${landed}/${total}`, ' landed'] as const,
  statTokens: (tokens: number) => `${compact(tokens)} tokens`,
  exitLabel: (met: number, total: number) => `Exit · ${met} of ${total}`,
  met: 'Met',
  notMet: 'Not met',
  metOn: (on: string) => `met ${on}`,
  issuesDone: 'its issues are done',
  doneOf: (done: number, named: number) => `${done} of ${named} done`,
  notMetNote: 'not met',
  waitsOnYou: 'Waits on you',
  waitsOn: 'Waits on',
  noIssueYet: 'No issue filed yet.',

  // The issues' table
  colIssue: 'Issue',
  colState: 'State',
  colTokens: 'Tokens',
  colAgents: 'Agents',
  none: '—',
  engine: { claude: 'Claude', codex: 'Codex', local: 'Local model' } as Record<string, string>,
  /** NEW: an engine and the full ids of the models it ran (Poulpe-761). */
  agentTitle: (engine: string, ids: readonly string[]) => (ids.length > 0 ? `${engine}: ${ids.join(', ')}` : `${engine}: model not known`),
  unknownIssue: 'No issue has this key on this board',

  // Below the phases
  elsewhereNote: 'Listed by this plan outside its phases.',
  annexNote: 'The full record, folded.',

  // A body off the structure
  fallback: 'Shown as written: part of this plan does not fit the plan layout.',
  notes: 'Drawn with notes',
}

/** Listing an issue in an open plan, under one of its phases or a new one, and moving it or taking it out: the pickers and the buttons of the issue page's Plans row. */
export const placeWords = {
  plans: 'Plans',
  noPlan: 'No plan lists it',
  addToPlan: 'Add to plan',
  plan: 'Plan',
  phase: 'Phase',
  newPhase: 'New phase…',
  phaseName: 'Phase name',
  add: 'Add',
  cancel: 'Cancel',
  reading: 'Loading plans',
  noPlans: 'No open plan to add it to.',
  change: 'Change',
  changeIn: (plan: string) => `Change its place in ${plan}`,
  move: 'Move',
  remove: 'Remove from plan',
}

/** The Plans tab and a plan's page. */
export const pageWords = {
  title: 'Plans',
  noPlans: 'No plans yet.',
  show: 'Show',
  hide: 'Hide',
  listedIssues: 'Listed issues',
  board: 'Board',
  claimedBy: (name: string) => `Claimed by ${name}`,
  /** NEW: the Plans tab's button, and the form it opens. */
  newPlan: 'New plan',
  /** NEW: the new plan's title field. */
  planTitle: 'Plan title',
  /** NEW: the field's placeholder. */
  titlePlaceholder: 'What the plan delivers',
  save: 'Save',
  saving: 'Saving…',
  plan: 'Plan',
  formerNumber: 'Its former Linear number',
  untitled: 'Untitled',
  summaryPlaceholder: 'Add a one-line summary',
  status: 'Status',
  planStatus: 'Plan status',
  showOnBoard: 'Show on board',
  progress: 'Progress',
  claim: 'Claim',
  unclaimed: 'Unclaimed',
  claimAction: 'Claim',
  unclaimAction: 'Unclaim',
  releaseClaim: 'Release claim',
  archived: 'This document is archived.',
  empty: 'This plan is empty.',
  editPlan: 'Edit plan',
  /** NEW: while the page reads a plan's body that changed. */
  loadingPlan: 'Loading plan',
  keys: '⌘S saves · Esc cancels',
  cancel: 'Cancel',
  merged: 'Saved and merged with a change made meanwhile.',
  htmlSource: 'HTML source',
  markdownSource: 'Markdown source',
  preview: 'Preview',
}

/** Comments on a plan, as today's comments say it. */
export const commentWords = {
  title: 'Comments',
  none: 'No comments yet.',
  leave: 'Leave a comment',
  reply: 'Reply',
  comment: 'Comment',
  edit: 'Edit',
  edited: '(edited)',
  /** NEW: beside the name of a comment the import brought from today's board. */
  imported: 'imported',
  editedAt: (when: string) => `Edited ${when}`,
  editComment: 'Edit comment',
  cancel: 'Cancel',
  save: 'Save',
  saving: 'Saving…',
  hint: (placeholder: string) => `${placeholder} (Markdown; ⌘↵ to send)`,
  fold: 'Fold this comment',
  openAll: 'Open all',
  foldEarlier: 'Fold earlier',
  replies: (n: number) => (n === 1 ? '1 reply' : `${n} replies`),
}

/** The Board's current plans and its plan filter (today's board/PlanStrip.tsx and FilterBar.tsx). */
export const stripWords = {
  current: 'Current plans',
  all: 'All plans',
  whole: 'Show the whole board',
  only: 'Show only the issues this plan lists',
  open: 'Open the plan',
  openNamed: (title: string) => `Open ${title}`,
  listed: 'Listed issues',
  chip: (title: string, status: string, done: number, total: number) => `${title}, ${status}, ${done} of ${total} issues done`,
  unknown: 'Unknown plan',
}
